import classnames from 'classnames';
import dompurify from 'dompurify';
import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import { Dialog } from 'primereact/dialog';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Tutorial from '../tutorial';
import { WrappedButton, WrappedButtonType } from '../ui/button';
import Utils from '../utils';
import { ModalButtonActionType } from './ModalButtonActionType.enum';

const { Util } = Tutorial;
const { withLayoutAndModalContexts } = Utils;

class TutorialModal extends Component {
	constructor(props) {
		super(props);

		// set up the tutorial guide, and the step view
		this.state = {
			guide: isArray(props.guide)
				? props.guide.map((_tip, index) => {
						const isLast = index === props.guide.length - 1;

						return {
							..._tip,
							modal: isBoolean(_tip.modal) ? _tip.modal : true, // default to `true` if not defined
							buttons: isArray(_tip.buttons)
								? _tip.buttons
								: [
										{
											type: isLast
												? ModalButtonActionType.End
												: ModalButtonActionType.Ok,
											text: isLast ? 'Done' : 'Next',
										},
									],
							step: index,
							isLast,
						};
					})
				: [],
			content: null,
			modalPosition: null,
			step: isNumber(props.step) ? props.step : 0,
		};
	}

	componentDidMount() {
		const { layoutContext } = this.props;
		const { guide, step } = this.state;

		this.setState(
			{
				content: guide[step],
			},
			() => {
				/**
				 * Use setTimeout because PrimereactDialog uses CSSTransition component to render
				 * the element. We can only calcualte the position AFTER the DOM is rendered so
				 * that getBoundingClientRect() can return the correct dimensions.
				 */
				setTimeout(() => this.updateModalPosition(this.onShow), 200);
			}
		);

		// update the modal position when window is resized
		layoutContext.subscribeLayoutUpdateEvent(
			'TutorialModal',
			this.updateModalPosition
		);
	}

	componentWillUnmount() {
		const { layoutContext } = this.props;

		layoutContext.unsubscribeLayoutUpdateEvent(
			'TutorialModal',
			this.updateModalPosition
		);
	}

	/**
	 * End the tutorial flow.
	 */
	end = () => {
		const { modalId, modalContext, onEnd } = this.props;

		modalContext.closeTutorial(modalId, onEnd);
	};

	/**
	 * Show the tip view.
	 * @param {number} step The tip index in the guide.
	 */
	showStep = step => {
		const { guide } = this.state;

		/**
		 * Dev note:
		 * DO NOT update `content` and `modalPosition` in one setState call. `modalPosition` needs
		 * to be updated after `content` so that we get the correct modal dimensions information
		 * for calculating the modal position.
		 */

		if (isObject(guide[step])) {
			this.setState(
				{
					content: guide[step],
					step,
				},
				() => {
					this.updateModalPosition(this.onShow);
				}
			);
		} else {
			// end the tutorial if there are no more tips to show
			this.end();
		}
	};

	/**
	 * Resume the tutorial flow after each button click.
	 *
	 * @param {ModalButtonActionType} actionType The type associated with the button.
	 */
	resumeAfterButtonClick = actionType => {
		const { step } = this.state;

		if (actionType === ModalButtonActionType.End) {
			this.end();
		} else {
			this.showStep(step + 1);
		}
	};

	/**
	 * Update modal position.
	 * @param {func} callback (Optional)
	 */
	updateModalPosition = callback => {
		const { content } = this.state;

		if (content) {
			this.setState(
				{
					modalPosition: Util.calculateModalPosition(content),
				},
				callback
			);
		}
	};

	/**
	 * Button click handler.
	 * @param {object} button The clicked button.
	 */
	onButtonClick = ({ type = ModalButtonActionType.Ok, onClick }) => {
		// call the onClick handler associated with the button if onClick is provided, otherwise, resume the flow
		if (isFunction(onClick)) {
			onClick({
				resume: () => this.resumeAfterButtonClick(type),
				end: this.end,
			});
		} else {
			this.resumeAfterButtonClick(type);
		}
	};

	/**
	 * Hide event handler.
	 * This is triggered when clicking 'X' in modal header.
	 */
	onHide = () => {
		// end tutorial
		this.end();
	};

	/**
	 * Callback to notify the parent each time the tutorial step is shown.
	 */
	onShow = () => {
		const { onShow } = this.props;
		const { content } = this.state;

		// function to call when the tutorial modal is shown
		if (isFunction(onShow)) {
			onShow(content);
		}
	};

	renderHeader = title => {
		if (isString(title)) {
			return (
				<div className="tutorial-modal-title">
					<h1>{title}</h1>
					<WrappedButton
						buttonType={WrappedButtonType.Icon}
						icon="pi pi-times"
						onClick={this.onHide}
						dataqa="tutorial-hide"
					/>
				</div>
			);
		}
	};

	// render buttons for the modal footer
	renderFooter = buttons => {
		if (isArray(buttons) && buttons.length > 0) {
			return (
				<div className="tutorial-modal-footer">
					{buttons.map((button, index) => (
						<WrappedButton
							key={`${index}-${button.text}`}
							buttonType={
								index === 0
									? WrappedButtonType.Primary
									: WrappedButtonType.Secondary
							}
							label={button.text}
							onClick={() => this.onButtonClick(button)}
							dataqa={index === 0 ? 'modal-primary' : 'modal-secondary'}
						/>
					))}
				</div>
			);
		}

		return null;
	};

	/**
	 * `dangerouslySetInnerHTML` is React's replacement for using `innerHtml` in the browser DOM.
	 * It is advised to use cautiously, so we're utilizing `DOMPurify` node package to strengthen
	 * the security. For more information, visit http://jira/browse/WEBAPPS-19690.
	 */
	renderMessage = message => (
		<div
			className="tutorial-modal-body"
			data-qa="tutorial-modal-desc"
			{...{ dataqa: 'tutorial-modal-desc' }}
			// eslint-disable-next-line
			dangerouslySetInnerHTML={{
				__html: isString(message) ? dompurify.sanitize(message) : '',
			}}
		/>
	);

	// render modal arrow based on modalPosition object
	renderArrow = modalPosition => {
		if (isObject(modalPosition) && isObject(modalPosition.arrow)) {
			const arrowCls = classnames('tutorial-modal-arrow', {
				south: modalPosition.arrow.direction === 'south',
				west: modalPosition.arrow.direction === 'west',
				east: modalPosition.arrow.direction === 'east',
				north: modalPosition.arrow.direction === 'north',
			});

			return <div className={arrowCls} style={modalPosition.arrow.position} />;
		}

		return null;
	};

	render() {
		const { content, modalPosition } = this.state;

		if (isObject(content)) {
			return (
				<Dialog
					style={
						isObject(modalPosition)
							? {
									position: 'fixed',
									top: modalPosition.top,
									left: modalPosition.left,
								}
							: null
					}
					className="modal tutorial-modal"
					showHeader={false}
					visible
					modal={content.modal === true}
					onHide={this.onHide}
					appendTo={document.getElementById('modals')}
				>
					{this.renderHeader(content.title)}
					{this.renderMessage(content.message)}
					{this.renderFooter(content.buttons)}
					{this.renderArrow(modalPosition)}
				</Dialog>
			);
		}

		return null;
	}
}

// props defaults
TutorialModal.defaultProps = {
	guide: [],
	step: 0,
};

// PropTypes
TutorialModal.propTypes = {
	// tutorial guide
	guide: PropTypes.array,

	// step of the tour
	step: PropTypes.number,

	// callback when the tutorial content (of one step) is shown
	onShow: PropTypes.func,

	// callback when the entire guide has shown and the tutorial has ended
	onEnd: PropTypes.func,
};

export default withLayoutAndModalContexts(TutorialModal);
